import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Edit, MoreHorizontal, Trash2, Eye, Calendar, MapPin, Phone, Mail, Package, User, CreditCard } from "lucide-react";
import { format } from "date-fns";
import type { Order } from "@/hooks/useOrders";
import { generateOrderPDF } from "@/utils/pdfGenerator";

interface OrdersTableProps {
  orders: Order[];
  onUpdateOrder: (orderId: string, orderData: Partial<Order>) => Promise<void>;
  onDeleteOrder: (orderId: string) => Promise<void>;
  getStatusBadge: (status: string) => React.ReactNode;
  artistDetails: any[]; // Add artistDetails prop
}

export function OrdersTable({ orders, onUpdateOrder, onDeleteOrder, getStatusBadge, artistDetails }: OrdersTableProps) {
  const [editingOrder, setEditingOrder] = useState<Order | null>(null);
  const [viewingOrder, setViewingOrder] = useState<Order | null>(null);
  const [editForm, setEditForm] = useState<Partial<Order>>({});
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [orderToDelete, setOrderToDelete] = useState<Order | null>(null);

  const handleEdit = (order: Order) => {
    setEditingOrder(order);
    setEditForm({
      order_status: order.order_status,
      tracking_number: order.tracking_number,
      shipping_carrier: order.shipping_carrier,
      estimated_delivery_date: order.estimated_delivery_date,
      expected_production_date: order.expected_production_date,
      notes: order.notes,
    });
    setIsEditDialogOpen(true);
  };

  const handleView = (order: Order) => {
    setViewingOrder(order);
    setIsViewDialogOpen(true);
  };

  const handleDelete = (order: Order) => {
    setOrderToDelete(order);
    setIsDeleteDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    if (editingOrder && editForm) {
      // Filter out undefined/null values and only send valid data
      const filteredEditForm = Object.fromEntries(
        Object.entries(editForm).filter(([_, value]) => value !== undefined && value !== null && value !== '')
      );
      
      console.log('🔧 Updating order with data:', filteredEditForm);
      
      try {
        await onUpdateOrder(editingOrder.id, filteredEditForm);
        setIsEditDialogOpen(false);
        setEditingOrder(null);
        setEditForm({});
      } catch (error) {
        console.error('❌ Error updating order:', error);
        // Don't close the dialog on error so user can see the error and retry
      }
    }
  };

  const confirmDelete = async () => {
    if (orderToDelete) {
      await onDeleteOrder(orderToDelete.id);
      setIsDeleteDialogOpen(false);
      setOrderToDelete(null);
    }
  };

  const getPaymentStatusBadge = (status: string) => {
    const variants: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
      pending: "secondary",
      completed: "default",
      failed: "destructive",
      cancelled: "destructive",
      refunded: "outline",
    };

    return (
      <Badge variant={variants[status] || "outline"}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), "MMM dd, yyyy");
    } catch {
      return "Invalid date";
    }
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
    }).format(price);
  };


  return (
    <>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Order ID</TableHead>
              <TableHead>Customer</TableHead>
              <TableHead>Product</TableHead>
              <TableHead>Amount</TableHead>
              <TableHead>Order Status</TableHead>
              <TableHead>Payment Status</TableHead>
              <TableHead>Order Date</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {orders.map((order) => (
              <TableRow key={order.id}>
                <TableCell className="font-mono text-sm">
                  {order.id.slice(0, 8)}...
                </TableCell>
                <TableCell>
                  <div>
                    <div className="font-medium">
                      {order.customer_first_name} {order.customer_last_name}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {order.customer_email}
                    </div>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-3">
                    {order.product_main_image && (
                      <img 
                        src={order.product_main_image} 
                        alt={order.product_name}
                        className="w-10 h-10 object-cover rounded-md border"
                        onError={(e) => {
                          e.currentTarget.src = '/placeholder.svg';
                        }}
                      />
                    )}
                    <div>
                      <div className="font-medium">{order.product_name}</div>
                      <div className="text-sm text-muted-foreground">
                        {order.product_category}
                      </div>
                      {order.selected_size_name && (
                        <div className="text-xs text-muted-foreground">
                          Variant: {order.selected_size_name}
                        </div>
                      )}
                    </div>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="font-medium">{formatPrice(order.payment_amount)}</div>
                </TableCell>
                <TableCell>{getStatusBadge(order.order_status)}</TableCell>
                <TableCell>{getPaymentStatusBadge(order.payment_status)}</TableCell>
                <TableCell>{formatDate(order.order_date)}</TableCell>
                <TableCell>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" className="h-8 w-8 p-0">
                        <span className="sr-only">Open menu</span>
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuLabel>Actions</DropdownMenuLabel>
                      <DropdownMenuItem onClick={() => handleView(order)}>
                        <Eye className="mr-2 h-4 w-4" />
                        View Details
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleEdit(order)}>
                        <Edit className="mr-2 h-4 w-4" />
                        Edit Order
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={() => handleDelete(order)}
                        className="text-red-600"
                      >
                        <Trash2 className="mr-2 h-4 w-4" />
                        Delete Order
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Edit Order Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Order</DialogTitle>
            <DialogDescription>
              Update order details and status
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="order_status">Order Status</Label>
                <Select
                  value={editForm.order_status}
                  onValueChange={(value) => setEditForm({ ...editForm, order_status: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending_payment">Pending Payment</SelectItem>
                    <SelectItem value="payment_confirmed">Payment Confirmed</SelectItem>
                    <SelectItem value="order_assigned">Order Assigned to Artist/Seller</SelectItem>
                    <SelectItem value="design_production">Design/Production in Progress</SelectItem>
                    <SelectItem value="quality_check">Quality Check</SelectItem>
                    <SelectItem value="ready_dispatch">Ready for Dispatch</SelectItem>
                    <SelectItem value="shipped">Shipped</SelectItem>
                    <SelectItem value="delivered">Delivered</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="cancelled_refunded">Cancelled/Refunded</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="expected_production_date">Expected Production Date</Label>
                <Input
                  id="expected_production_date"
                  type="date"
                  value={editForm.expected_production_date || ""}
                  onChange={(e) => setEditForm({ ...editForm, expected_production_date: e.target.value })}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="tracking_number">Tracking Number</Label>
                <Input
                  id="tracking_number"
                  value={editForm.tracking_number || ""}
                  onChange={(e) => setEditForm({ ...editForm, tracking_number: e.target.value })}
                  placeholder="Enter tracking number"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="shipping_carrier">Shipping Carrier</Label>
                <Input
                  id="shipping_carrier"
                  value={editForm.shipping_carrier || ""}
                  onChange={(e) => setEditForm({ ...editForm, shipping_carrier: e.target.value })}
                  placeholder="Enter shipping carrier"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="estimated_delivery_date">Estimated Delivery Date</Label>
              <Input
                id="estimated_delivery_date"
                type="date"
                value={editForm.estimated_delivery_date || ""}
                onChange={(e) => setEditForm({ ...editForm, estimated_delivery_date: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={editForm.notes || ""}
                onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                placeholder="Add any additional notes"
                rows={3}
              />
            </div>
          </div>
          <div className="flex justify-end space-x-2">
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveEdit}>Save Changes</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* View Order Dialog */}
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Order Details</DialogTitle>
            <DialogDescription>
              Complete information about this order
            </DialogDescription>
          </DialogHeader>
          {viewingOrder && (
            <div className="space-y-6">
              {/* Order Header */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-muted rounded-lg">
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">Order ID</Label>
                  <p className="font-mono text-sm">{viewingOrder.id}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">Order Date</Label>
                  <p className="text-sm">{formatDate(viewingOrder.order_date)}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">Order Status</Label>
                  <div className="mt-1">{getStatusBadge(viewingOrder.order_status)}</div>
                </div>
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">Payment Status</Label>
                  <div className="mt-1">{getPaymentStatusBadge(viewingOrder.payment_status)}</div>
                </div>
              </div>

              {/* Customer Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <Mail className="h-5 w-5" />
                  Customer Information
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">Name</Label>
                    <p className="text-sm">
                      {viewingOrder.customer_first_name} {viewingOrder.customer_last_name}
                    </p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">Email</Label>
                    <p className="text-sm">{viewingOrder.customer_email}</p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">Phone</Label>
                    <p className="text-sm flex items-center gap-1">
                      <Phone className="h-4 w-4" />
                      {viewingOrder.customer_phone}
                    </p>
                  </div>
                </div>
              </div>

              {/* Product Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <Package className="h-5 w-5" />
                  Product Information
                </h3>
                
                {/* Product Preview with Image */}
                {viewingOrder.product_main_image && (
                  <div className="mb-4">
                    <Label className="text-sm font-medium text-muted-foreground mb-2 block">Product Preview</Label>
                    <div className="flex items-center gap-4 p-3 border rounded-lg bg-muted/50">
                      <img 
                        src={viewingOrder.product_main_image} 
                        alt={viewingOrder.product_name}
                        className="w-16 h-16 object-cover rounded-md border"
                        onError={(e) => {
                          e.currentTarget.src = '/placeholder.svg';
                        }}
                      />
                      <div className="flex-1">
                        <p className="font-medium text-sm">{viewingOrder.product_name}</p>
                        {viewingOrder.selected_size_name && (
                          <p className="text-xs text-muted-foreground">
                            Variant: <span className="font-medium">{viewingOrder.selected_size_name}</span>
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">Product Name</Label>
                    <p className="text-sm">{viewingOrder.product_name}</p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">Product ID</Label>
                    <p className="text-sm font-mono">{viewingOrder.product_id}</p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">Category</Label>
                    <p className="text-sm">{viewingOrder.product_category}</p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">Price</Label>
                    <p className="text-sm">{formatPrice(viewingOrder.product_price)}</p>
                  </div>
                  {viewingOrder.selected_size_name && (
                    <div>
                      <Label className="text-sm font-medium text-muted-foreground">Selected Variant</Label>
                      <p className="text-sm font-medium">{viewingOrder.selected_size_name}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Artist Details */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <User className="h-5 w-5" />
                  Artist Details
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">Artist ID</Label>
                    <p className="text-sm font-mono">
                      {viewingOrder.product_artistid || "No Artist Assigned"}
                    </p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">Artist Name</Label>
                    <p className="text-sm">
                      {(() => {
                        if (viewingOrder.product_artistid) {
                          const artist = artistDetails.find(a => a.artistid === viewingOrder.product_artistid);
                          return artist ? artist.full_name : "Artist not found";
                        }
                        return "No Artist Assigned";
                      })()}
                    </p>
                  </div>
                </div>
              </div>

              {/* Delivery Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <MapPin className="h-5 w-5" />
                  Delivery Information
                </h3>
                <div className="space-y-2">
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">Address</Label>
                    <p className="text-sm">{viewingOrder.delivery_address}</p>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label className="text-sm font-medium text-muted-foreground">City</Label>
                      <p className="text-sm">{viewingOrder.delivery_city}</p>
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-muted-foreground">State</Label>
                      <p className="text-sm">{viewingOrder.delivery_state}</p>
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-muted-foreground">Pincode</Label>
                      <p className="text-sm">{viewingOrder.delivery_pincode}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Payment Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <CreditCard className="h-5 w-5" />
                  Payment Information
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">Payment Method</Label>
                    <p className="text-sm">{viewingOrder.payment_method}</p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">Amount</Label>
                    <p className="text-sm font-medium">{formatPrice(viewingOrder.payment_amount)}</p>
                  </div>
                  {viewingOrder.transaction_id && (
                    <div>
                      <Label className="text-sm font-medium text-muted-foreground">Transaction ID</Label>
                      <p className="text-sm font-mono">{viewingOrder.transaction_id}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Additional Information */}
              {(viewingOrder.custom_photo_url || viewingOrder.custom_text || viewingOrder.special_remarks || viewingOrder.notes) && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold">Additional Information</h3>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        // Create a comprehensive text file with all custom information
                        const content = [
                          `Order ID: ${viewingOrder.id}`,
                          `Customer: ${viewingOrder.customer_first_name} ${viewingOrder.customer_last_name}`,
                          `Product: ${viewingOrder.product_name}`,
                          `Order Date: ${formatDate(viewingOrder.order_date)}`,
                          '',
                          '--- CUSTOM INFORMATION ---',
                          '',
                          viewingOrder.custom_text ? `Custom Text: ${viewingOrder.custom_text}` : '',
                          viewingOrder.custom_text ? '' : '',
                          viewingOrder.special_remarks ? `Special Remarks: ${viewingOrder.special_remarks}` : '',
                          viewingOrder.special_remarks ? '' : '',
                          viewingOrder.notes ? `Admin Notes: ${viewingOrder.notes}` : '',
                          viewingOrder.custom_photo_url ? `Custom Photo URL: ${viewingOrder.custom_photo_url}` : '',
                        ].filter(line => line !== '').join('\n');
                        
                        const blob = new Blob([content], { type: 'text/plain' });
                        const url = URL.createObjectURL(blob);
                        const link = document.createElement('a');
                        link.href = url;
                        link.download = `order_details_${viewingOrder.id}.txt`;
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                        URL.revokeObjectURL(url);
                      }}
                    >
                      Download All Info
                    </Button>
                  </div>
                  <div className="space-y-4">
                    {viewingOrder.custom_photo_url && (
                      <div>
                        <Label className="text-sm font-medium text-muted-foreground">Custom Photo</Label>
                        <div className="mt-2 space-y-2">
                          <img
                            src={viewingOrder.custom_photo_url}
                            alt="Custom photo"
                            className="w-32 h-32 object-cover rounded-lg border"
                          />
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const link = document.createElement('a');
                              link.href = viewingOrder.custom_photo_url!;
                              link.download = `custom_photo_${viewingOrder.id}.jpg`;
                              document.body.appendChild(link);
                              link.click();
                              document.body.removeChild(link);
                            }}
                            className="w-full"
                          >
                            Download Photo
                          </Button>
                        </div>
                      </div>
                    )}
                    {viewingOrder.custom_text && (
                      <div>
                        <Label className="text-sm font-medium text-muted-foreground">Custom Text</Label>
                        <div className="mt-2 space-y-2">
                          <p className="text-sm bg-muted p-2 rounded border">{viewingOrder.custom_text}</p>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const blob = new Blob([viewingOrder.custom_text!], { type: 'text/plain' });
                              const url = URL.createObjectURL(blob);
                              const link = document.createElement('a');
                              link.href = url;
                              link.download = `custom_text_${viewingOrder.id}.txt`;
                              document.body.appendChild(link);
                              link.click();
                              document.body.removeChild(link);
                              URL.revokeObjectURL(url);
                            }}
                            className="w-full"
                          >
                            Download Text
                          </Button>
                        </div>
                      </div>
                    )}
                    {viewingOrder.special_remarks && (
                      <div>
                        <Label className="text-sm font-medium text-muted-foreground">Special Remarks</Label>
                        <div className="mt-2 space-y-2">
                          <p className="text-sm bg-muted p-2 rounded border">{viewingOrder.special_remarks}</p>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const blob = new Blob([viewingOrder.special_remarks!], { type: 'text/plain' });
                              const url = URL.createObjectURL(blob);
                              const link = document.createElement('a');
                              link.href = url;
                              link.download = `special_remarks_${viewingOrder.id}.txt`;
                              document.body.appendChild(link);
                              link.click();
                              document.body.removeChild(link);
                              URL.revokeObjectURL(url);
                            }}
                            className="w-full"
                          >
                            Download Remarks
                          </Button>
                        </div>
                      </div>
                    )}
                    {viewingOrder.notes && (
                      <div>
                        <Label className="text-sm font-medium text-muted-foreground">Admin Notes</Label>
                        <div className="mt-2 space-y-2">
                          <p className="text-sm bg-muted p-2 rounded border">{viewingOrder.notes}</p>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const blob = new Blob([viewingOrder.notes!], { type: 'text/plain' });
                              const url = URL.createObjectURL(blob);
                              const link = document.createElement('a');
                              link.href = url;
                              link.download = `admin_notes_${viewingOrder.id}.txt`;
                              document.body.appendChild(link);
                              link.click();
                              document.body.removeChild(link);
                              URL.revokeObjectURL(url);
                            }}
                            className="w-full"
                          >
                            Download Notes
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Shipping Information */}
              {(viewingOrder.tracking_number || viewingOrder.shipping_carrier || viewingOrder.estimated_delivery_date || viewingOrder.expected_production_date) && (
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold flex items-center gap-2">
                    <Calendar className="h-5 w-5" />
                    Shipping Information
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    {viewingOrder.tracking_number && (
                      <div>
                        <Label className="text-sm font-medium text-muted-foreground">Tracking Number</Label>
                        <p className="text-sm font-mono">{viewingOrder.tracking_number}</p>
                      </div>
                    )}
                    {viewingOrder.shipping_carrier && (
                      <div>
                        <Label className="text-sm font-medium text-muted-foreground">Shipping Carrier</Label>
                        <p className="text-sm">{viewingOrder.shipping_carrier}</p>
                      </div>
                    )}
                    {viewingOrder.estimated_delivery_date && (
                      <div>
                        <Label className="text-sm font-medium text-muted-foreground">Estimated Delivery</Label>
                        <p className="text-sm">{formatDate(viewingOrder.estimated_delivery_date)}</p>
                      </div>
                    )}
                    {viewingOrder.expected_production_date && (
                      <div>
                        <Label className="text-sm font-medium text-muted-foreground">Expected Production</Label>
                        <p className="text-sm">{formatDate(viewingOrder.expected_production_date)}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Download PDF Button */}
              <div className="flex justify-center pt-6 border-t">
                <Button
                  onClick={async () => await generateOrderPDF(viewingOrder)}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  Download Details
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Order</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this order? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end space-x-2">
            <Button variant="outline" onClick={() => setIsDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={confirmDelete}>
              Delete Order
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
